import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Trophy, Wifi, WifiOff, Users2, Eye } from 'lucide-react';
import { SponsorBanner } from '@/components/SponsorBanner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_BASE = process.env.REACT_APP_BACKEND_URL.replace(/^https/, 'wss').replace(/^http/, 'ws');
const TGA_LOGO = "https://customer-assets.emergentagent.com/job_853d16bb-77c9-4e1d-85ff-db52f6af924e/artifacts/lsv7codb_Copy%20of%20TGA%20pro%20tour%20logo%20transparent.png";

const SpectatePage = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);

  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const reconnectAttempts = useRef(0);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API_URL}/tournaments/${id}/spectate`);
        setData(res.data);
        setLeaderboard(res.data.leaderboard);
      } catch (err) {
        setError(err.response?.status === 404 ? 'Tournament not found' : 'Failed to load');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // WebSocket for live updates
  useEffect(() => {
    if (!id) return;

    const connectWs = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) return;
      const socket = new WebSocket(`${WS_BASE}/api/tournaments/${id}/ws`);
      wsRef.current = socket;

      socket.onopen = () => {
        setWsConnected(true);
        reconnectAttempts.current = 0;
      };
      socket.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'leaderboard_update') {
            setLeaderboard(msg.leaderboard);
          }
        } catch {}
      };
      socket.onclose = () => {
        setWsConnected(false);
        wsRef.current = null;
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
        reconnectAttempts.current += 1;
        reconnectTimer.current = setTimeout(connectWs, delay);
      };
      socket.onerror = () => socket.close();
    };

    connectWs();
    const ping = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send('ping');
    }, 25000);

    return () => {
      clearInterval(ping);
      clearTimeout(reconnectTimer.current);
      if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); }
    };
  }, [id]);

  const formatScore = (s) => (s === 0 ? 'E' : s > 0 ? `+${s}` : `${s}`);

  const getRankBadge = (rank) => {
    if (rank === 1) return <span className="rank-gold px-2 py-1 rounded text-xs font-bold">1st</span>;
    if (rank === 2) return <span className="rank-silver px-2 py-1 rounded text-xs font-bold">2nd</span>;
    if (rank === 3) return <span className="rank-bronze px-2 py-1 rounded text-xs font-bold">3rd</span>;
    return <span className="text-muted-foreground font-mono">{rank}</span>;
  };

  const getThru = (completed, total) => {
    if (completed === 0) return '-';
    if (completed >= total) return 'F';
    return completed.toString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-pulse text-primary text-xl font-heading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <Card className="bg-card border-white/10 w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-heading text-white mb-2">{error}</h2>
            <p className="text-muted-foreground text-sm">This tournament may not exist or has been removed.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isTeam = data?.is_team;
  const numHoles = data?.num_holes || 0;

  return (
    <div className="min-h-screen bg-[#050505]">
      <SponsorBanner variant="top-banner" />

      {/* Header */}
      <nav className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Eye className="w-5 h-5 text-muted-foreground" />
            <span className="text-muted-foreground text-sm font-medium">Spectator Mode</span>
          </div>
          <img src={TGA_LOGO} alt="TGA Pro Tour" className="h-10 object-contain" />
          <div data-testid="spectate-ws-status">
            {wsConnected ? (
              <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-500/15 text-green-400 border border-green-500/30">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                LIVE
              </span>
            ) : (
              <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-zinc-500/15 text-zinc-400 border border-zinc-500/30">
                <WifiOff className="w-3 h-3" />
                Connecting...
              </span>
            )}
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Tournament Title */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-heading text-white mb-2" data-testid="spectate-tournament-name">
            {data?.tournament_name}
          </h1>
          <div className="flex items-center justify-center gap-3 text-sm text-muted-foreground">
            <span>Par {data?.total_par}</span>
            <span className="w-1 h-1 rounded-full bg-muted-foreground" />
            <span>{numHoles} Holes</span>
            {isTeam && (
              <>
                <span className="w-1 h-1 rounded-full bg-muted-foreground" />
                <span className="text-secondary">
                  {data.team_format === 'best_ball' ? 'Best Ball' :
                   data.team_format === 'combined' ? 'Combined' : 'Alternate Shot'}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Leaderboard */}
        <Card className="bg-card border-white/10 overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Trophy className="w-5 h-5 text-primary" />
              {isTeam ? 'Team Leaderboard' : 'Leaderboard'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {leaderboard.length === 0 ? (
              <div className="p-12 text-center">
                <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-heading text-white mb-2">Waiting for Scores</h3>
                <p className="text-muted-foreground text-sm">The leaderboard will update live as players submit scores.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                {/* Header */}
                <div className="grid grid-cols-[50px_1fr_60px_60px_70px] sm:grid-cols-[60px_1fr_80px_80px_80px] items-center px-4 py-3 border-b border-white/10 font-heading text-sm text-muted-foreground">
                  <div>Rank</div>
                  <div>{isTeam ? 'Team' : 'Player'}</div>
                  <div className="text-center">Thru</div>
                  <div className="text-right">Total</div>
                  <div className="text-right">To Par</div>
                </div>

                {/* Rows */}
                {leaderboard.map((entry, index) => {
                  const thru = getThru(entry.holes_completed, numHoles);
                  return (
                    <div
                      key={entry.team_id || entry.member_id || index}
                      className="grid grid-cols-[50px_1fr_60px_60px_70px] sm:grid-cols-[60px_1fr_80px_80px_80px] items-center px-4 py-3 border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                      data-testid={`spectate-row-${index}`}
                    >
                      <div>{getRankBadge(entry.rank)}</div>
                      <div className="min-w-0">
                        {isTeam ? (
                          <>
                            <div className="flex items-center gap-1.5">
                              <Users2 className="w-3.5 h-3.5 text-secondary shrink-0" />
                              <span className="text-white font-medium truncate">{entry.team_name}</span>
                            </div>
                            <div className="text-muted-foreground text-xs truncate">{entry.captain_name} & {entry.partner_name}</div>
                          </>
                        ) : (
                          <>
                            <div className="text-white font-medium truncate">{entry.member_name}</div>
                            <div className="text-muted-foreground text-xs">{entry.member_number}</div>
                          </>
                        )}
                      </div>
                      <div className="text-center">
                        <span className={`font-mono text-sm ${thru === 'F' ? 'text-secondary font-bold' : 'text-muted-foreground'}`}>
                          {thru}
                        </span>
                      </div>
                      <div className="text-right font-mono text-lg text-white">
                        {entry.total_strokes || '-'}
                      </div>
                      <div className={`text-right font-mono text-lg font-bold ${
                        entry.score_to_par < 0 ? 'text-secondary text-glow-green' :
                        entry.score_to_par === 0 ? 'text-primary' : 'text-destructive'
                      }`}>
                        {entry.holes_completed > 0 ? (entry.score_label || formatScore(entry.score_to_par)) : '-'}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-muted-foreground text-xs">
            Powered by TGA Pro Tour — Table Golf Association
          </p>
        </div>
      </main>
    </div>
  );
};

export default SpectatePage;
